import type { ComponentProps } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { mergeCurrentSearchIntoTo } from '../hooks/useDashboardNavigate';

export type DashboardNavigateProps = ComponentProps<typeof Navigate>;

/**
 * react-router's `<Navigate>` with the current query merged into `to` (DP-3188
 * AC8) — the declarative-redirect half of {@link useDashboardNavigate},
 * sharing its merge rule verbatim: destination wins on key collision, external
 * and anchor-only targets pass through untouched.
 *
 * A redirect is as much an in-app navigation as a click, and it is the one kind
 * that runs before the user can act — so a plain `<Navigate>` is where scope
 * silently disappears. The landing redirect (bare client route → default leaf)
 * is exactly that case: the first-login picker commits its answer onto the bare
 * route, and an unmerged redirect replaced the URL a tick later, dropping the
 * param the actor had just chosen.
 *
 * **Why the query is read from the address bar and not only from the router.**
 * A redirect can mount in the same batch as the navigation that produced the
 * query it is supposed to carry. When several history writes land in one tick —
 * the picker's commit, then this redirect — React coalesces the re-renders and
 * the router renders only the *final* location, so the committed query is never
 * observable through `useLocation()` at all: it exists in `window.location`,
 * and the router's snapshot skips straight past it. Merging from the router
 * alone therefore drops exactly the scope this component exists to preserve.
 *
 * The router's location stays preferred whenever it has a query — it is the
 * canonical source in normal operation, and the only source under
 * `MemoryRouter`, which has no address bar. The address bar is consulted solely
 * to fill the gap where the router reports nothing, which is precisely the
 * coalesced-batch case.
 */
function currentSearch(routerSearch: string): string {
  if (routerSearch) {
    return routerSearch;
  }
  if (typeof window === 'undefined') {
    return '';
  }
  // Re-encoded rather than read verbatim: the address bar is the one source here
  // that is not already router-normalised, and the result is spliced into a
  // redirect target. Parsing and re-serializing makes it a well-formed query
  // string by construction, whatever was typed.
  const reEncoded = new URLSearchParams(window.location.search).toString();
  return reEncoded ? `?${reEncoded}` : '';
}

export function DashboardNavigate({
  to,
  ...navigateProps
}: Readonly<DashboardNavigateProps>) {
  const { search } = useLocation();

  return (
    <Navigate
      to={mergeCurrentSearchIntoTo(to, currentSearch(search))}
      {...navigateProps}
    />
  );
}
